/**
 * Pulsing amber marker rendered as the `content` element on an
 * `AdvancedMarkerElement`. Decoupled from the marker instance so the same
 * DOM element can be reused across rerenders without re-creating the
 * marker (preserves Maps' internal reference equality for click handlers).
 *
 * Reduced-motion users get a static dot via the `prefers-reduced-motion`
 * media query in index.css's `@keyframes property-marker-pulse` declaration.
 */
import clsx from 'clsx';

export interface PropertyMapMarkerProps {
  className?: string;
}

/**
 * Returns a freshly-allocated `<div>` to use as the marker's `content`
 * element. Marker rendering is imperative (Maps API expects a real DOM node),
 * not React-driven — but we keep the styling tokens here for design-system
 * consistency. The caller (`<PropertyMap>`) attaches the returned node to
 * `AdvancedMarkerElement.content`.
 */
export function createPropertyMarkerElement(): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'property-marker';
  wrapper.setAttribute('data-testid', 'property-map-marker');
  // Inline class string mirrors the Tailwind tokens in markup form.
  // Pulsing amber dot — animation defined in apps/web/src/index.css under
  // @keyframes property-marker-pulse (reduced-motion guarded).
  wrapper.innerHTML = `
    <span class="property-marker-pulse" aria-hidden="true"></span>
    <span class="property-marker-core" aria-hidden="true"></span>
  `;
  return wrapper;
}

/**
 * React-component variant for unit-test rendering — never mounted in the
 * actual map context. Exists so tests can assert the visual structure
 * without driving Maps SDK internals.
 */
export function PropertyMapMarker({ className }: PropertyMapMarkerProps) {
  return (
    <div
      data-testid="property-map-marker"
      className={clsx('property-marker', className)}
    >
      <span className="property-marker-pulse" aria-hidden="true" />
      <span className="property-marker-core" aria-hidden="true" />
    </div>
  );
}
